import type { ISerialize } from '@/objects/ISerialize';
import {
    deregisterSignalrCallback as DeregisterSignalrCallback,
    invokeServerSideMethod as InvokeServerSideMethod,
    registerSignalrCallback as RegisterSignalrCallback,
} from './signalr';
import type { IBaseObjStaticConstructor } from '@/objects/IBaseObjStaticConstructor';
import { formatDateIso } from '@/utilities/dateutils';

/**
 * Represents a subscription to a signalr method
 *
 * This holds all the callbacks for one unique subscription so we can subscribe from several places in the frontend with the same parameters,
 * but only get one callback from the server
 *
 * This is unique per method + parameters combination
 *
 * We can however subscribe to the same method with different parameters, and that will create a new subscription
 */
export class SignalrSubscription<T extends ISerialize> {
    /**
     * The method to subscribe to
     */
    public readonly backendMethodName: string;

    /**
     * The parameters to the method
     */
    public readonly subscribeParameters: ISerialize;

    /**
     * This is the key the server generated when we subscribed.
     *
     * This is used to determine if the callback we get is actually ment for this subscription.
     *
     * The logic here is kinda hard to follow, but here goes:
     * We subscribe to a type/method, with some parameters.
     * The backend then generates a key from the method and parameters and saves the client subscription under that key.
     * When the server is going to do a callback it finds all the clients with a key that fits the parameteres of the data it wants to send
     * and invokes the frontend callback method.
     * The problem comes from the fact that the frontend callback method is invoked for ALL subscribers to that method, regardless of parameters.
     * So if we have subscribed to the type "MyMethod" with parameter A=1, THIS subscription instance will be for MyMethod-A=1.
     * If we also subscribe to "MyMethod" with parameter A=2, that will be in another subscription instance.
     * However, when the server is doing the callbacks, it will just call "MyMethod" in the frontend with whatever data it wants to notify for.
     * This will trigger both subscription instances since they are both listening to "MyMethod".
     * So we need a way to know if the supplied data is actually for this subscription instance when the server invokes the frontend method.
     *
     * So if we have more than one subscription to the same METHOD, all subscriptions will be invoked by the callback.
     * Only some of them should react to that specific invoke.
     *
     * Note that if we have only subscribed to A=1, then the server will not find the client for the A=2 case, and the client isnt notified at all. (which is what we want obviously)
     */
    private serverSideKey: string | null = null;

    /**
     * A list of callbacks to call when we receive data
     */
    private readonly callbacks: Set<(data: T[]) => void> = new Set();

    /**
     * If the subscription is allready invoked on the server
     */
    private isInvoked = false;

    /**
     * If we are in the process of invoking the server side method
     */
    private isInvoking = false;

    /**
     * Helper to generate the correct type from the backend data.
     */
    private type: IBaseObjStaticConstructor<T>;

    /**
     * Keep track of what the last message we received from the server was.
     * We use this to request any missing messages after a disconnect
     */
    private lastMessageReceivedSequenceNumber = 0;

    /**
     * If we disconnect we save the sequencenumber of the last message before the disconnect so we know what messages we missed
     */
    private lastMessageReceivedSequenceNumberWhenDisconnected: number | null = null;

    /**
     * Keep track of when we received the last message from the server so we can log it
     */
    private lastMessageReceivedTime: Date | null = null;

    /**
     * Injected function that we will call if we get a sequenceNumber that is lower than the previous sequenceNumber we received
     * This will mean that the server restarted, and the sequenceNumber has been reset to 0
     */
    private triggerSequenceNumberRegressionAlert: () => void;

    /**
     * Injected function that we will call if the server cant resend all the missed messages.
     */
    private triggerResendMissingMessagesAlert: () => void;

    /**
     * Generates an unique key for the subscription based on the method and parameters
     *
     * This is unique in the sense that if two subscriptions have the same method and parameters, they will have the same key.
     *
     * This key is DIFFERENT from the server side subscription key, which is generated by the server and returned when we subscribe.
     *
     * Both keys however follow the same rules, the rules being that unique parameters create a unique key
     *
     * This is used to hold the subscriptions in a map on the frontend side.
     * @param method The method we will call in the backend to subscribe to something
     * @param subscribeParameters The parameters for the subscription
     * @returns A unique key for the subscription
     */
    public static GenerateFrontendSubscriptionKey(method: string, subscribeParameters: ISerialize): string {
        return `${method}-${subscribeParameters.serialize()}`;
    }

    /**
     * Constructor for the Subscription class
     * @param method the method to subscribe to
     * @param subscribeParameters the parameters to the method
     * @param callback the callback to call when we receive data
     * @param triggerSequenceNumberRegressionAlert a callback to call when we detect that the sequence number of the data we receive is lower than the last one we received, which indicates that we have missed some data
     */
    constructor(
        type: IBaseObjStaticConstructor<T>,
        method: string,
        subscribeParameters: ISerialize,
        triggerSequenceNumberRegressionAlert: () => void,
        triggerResendMissingMessagesAlert: () => void,
    ) {
        this.backendMethodName = method;
        this.subscribeParameters = subscribeParameters;
        this.type = type;
        this.triggerSequenceNumberRegressionAlert = triggerSequenceNumberRegressionAlert;
        this.triggerResendMissingMessagesAlert = triggerResendMissingMessagesAlert;
    }

    /**
     * Add a new callback to the subscription
     * @param callback
     */
    public async addCallback(callback: (data: T[]) => void) {
        this.callbacks.add(callback);

        if (this.isInvoking) {
            return;
        }

        // If we have not already invoked the server side method, do so now
        if (!this.isInvoked) {
            await this.startServerSubscription();
            await this.registerSignalrCallback();
        }
    }

    /**
     * Remove a callback from the subscription
     * @param callback
     */
    public async removeCallback(callback: (data: T[]) => void) {
        this.callbacks.delete(callback);

        // We need to unsubscribe from the server if we dont have any callbacks left
        if (this.callbacks.size === 0) {
            // Remove the subscription from the server
            await this.deregisterSignalrCallback();
            await this.stopServerSubscription();
        }
    }

    public async notifyOfDisconnect() {
        // Copy the last message counter so it doesnt get overwritten once we have resubscribed and we get fresh messages from the server
        console.warn(
            'We disconnected Signalr. Last sequenceNumber at disconnect is',
            this.lastMessageReceivedSequenceNumber,
            'for group',
            this.serverSideKey,
        );
        this.lastMessageReceivedSequenceNumberWhenDisconnected = this.lastMessageReceivedSequenceNumber;
    }

    /**
     * Reregister with the server
     */
    public async resubscribe(args?: { reloadPotentiallyMissingMessages: boolean }) {
        console.log('Resubscribing to', this.backendMethodName, this.subscribeParameters);

        // First we make sure we are not subscribed
        await this.stopServerSubscription();

        // This will restart the subscription on the server
        await this.startServerSubscription();

        if (args?.reloadPotentiallyMissingMessages) {
            await this.requestMissingMessages();
        }
    }

    /**
     * Requests that the server resends messages we might have missed during disconnect
     * @param lastMessageSequenceNumber The last message confirmed received
     */
    private async requestMissingMessages() {
        const lastMessageSequenceNumber = this.lastMessageReceivedSequenceNumberWhenDisconnected;

        // If we dont have a sequenceNumber, we havent received any messages, so we dont need to request any missing messages
        if (lastMessageSequenceNumber === null) {
            return;
        }

        // If the last sequence number is 0, we disconnected before receiving any messages.
        // If we request missing messages starting from 0, we will get ALL messages from the server, so we cant really do that.
        // But if this happens we may still be missing some, we just dont know which ones.
        // This is also a reload the window to be sure case, but mostly this just wont happen unless you are very unlucky, so I think for now its okay to just return here
        if (lastMessageSequenceNumber == 0) {
            console.warn(
                'We disconnected before receiving any messages, so we dont know which messages we missed. We will not request any missing messages to avoid requesting all messages from the server. Group: ',
                this.serverSideKey,
            );
            return;
        }

        // Reset the number
        this.lastMessageReceivedSequenceNumberWhenDisconnected = null;

        console.log(
            'Requesting missing signalrMessage from last sequencenumber',
            lastMessageSequenceNumber,
            'for group',
            this.serverSideKey,
        );

        const resendSuccess = await InvokeServerSideMethod<boolean>(
            'Resend' + this.backendMethodName,
            this.serverSideKey,
            lastMessageSequenceNumber,
            this.lastMessageReceivedTime != null ? formatDateIso(this.lastMessageReceivedTime) : null,
        );

        if (!resendSuccess) {
            this.triggerResendMissingMessagesAlert();
        }
    }

    /**
     * Handling the callback from the backend
     * @param args
     */
    private backendCallback(data: T[]) {
        for (const callback of this.callbacks) {
            callback(data);
        }
    }

    /**
     * Start the subscription on the server
     */
    private async startServerSubscription() {
        if (this.isInvoked) {
            return;
        }

        this.isInvoking = true;

        try {
            // Note, we need to serialize to parse the enums, but InvokeServerSideMethod is expecting an object, so we end up with this ugly thing
            // TODO: Maybe make a method in the subscribeParameters objects to do this without falling back to JSON.parse?
            const params = JSON.parse(this.subscribeParameters.serialize());
            this.serverSideKey = await InvokeServerSideMethod<string>('Subscribe' + this.backendMethodName, params);
            this.isInvoked = true;
            this.isInvoking = false;
        } catch (e) {
            console.error('Could not connect to signalr: ', e);
            this.isInvoking = false;
        }
    }

    /**
     * Stop the subscription on the server
     */
    private async stopServerSubscription() {
        if (!this.isInvoked) {
            return;
        }

        try {
            // Note, we need to serialize to parse the enums, but InvokeServerSideMethod is expecting an object, so we end up with this ugly thing
            // TODO: Maybe make a method in the subscribeParameters objects to do this without falling back to JSON.parse?
            const params = JSON.parse(this.subscribeParameters.serialize());
            await InvokeServerSideMethod<void>('Unsubscribe' + this.backendMethodName, params);
            this.isInvoked = false;
        } catch (e) {
            console.error('Could not connect to signalr: ', e);
        }
    }

    /**
     * We need an internal callback to handle the data from the signalr connection. This will just convert the json to the correct type and call the backend callback
     * @param serverCallbackKey The key of the subscription the server is invoking the callback for
     * @param args The actual data arguments
     * @returns
     */
    private signalrCallbackWrapper = (serverCallbackKey: string, args: any[], sequenceNumber: number) => {
        // Skip callbacks for the wrong subscription
        if (serverCallbackKey !== this.serverSideKey) {
            return;
        }

        // If we get a negative messageCounter it means that we are resending earlier messages, so we should not update the lastMessageReceivedCounter
        // Note, if the backend restarts, we start over so we might get a messageCounter that is lower than the current lastMessageReceivedCounter.
        // If that happens we should still overwrite
        // The problem now is that we might request the same messages over and over if we disconnect several times, since the resend doesnt update the counter.
        // We can probably do this better. Like if we disconnect at sequenceNumber 100, then we miss 10 messages, and we reconnect, we get the last 10 messages again.
        // If there is no new signalrMessage, the sequenceNumber will stay at 100, so any later disconnects and reconnects will keep requesting the same 10 messages
        // I dont know how realistic this is. We can send an extra boolean indicating if its a resend, and only update the counter if the number is higher I guess.
        // And then ofc send the latest sequence of the ones we are resending from the serverside instead of -1
        if (sequenceNumber > 0) {
            // console.log(
            //     'Sequence number of received message: ',
            //     sequenceNumber,
            //     'Last message received sequence number: ',
            //     this.lastMessageReceivedSequenceNumber,
            //     'For group: ',
            //     this.serverSideKey,
            // );

            // If the last sequenceNumber is 0 we havent received any messages and we dont have any expectation for what the servers number should be, so dont log
            if (this.lastMessageReceivedSequenceNumber > 0) {
                if (sequenceNumber !== this.lastMessageReceivedSequenceNumber + 1) {
                    // This means we are missing a message
                    console.warn(
                        'We are expecting message with numbers in order. Last sequence number was: ',
                        this.lastMessageReceivedSequenceNumber,
                        'SequenceNumber for the message received now was: ',
                        sequenceNumber,
                        ' for group: ',
                        this.serverSideKey,
                    );
                }

                if (sequenceNumber < this.lastMessageReceivedSequenceNumber) {
                    console.warn(
                        `We got sequenceNumber ${sequenceNumber} from server. The last known for the fronted was ${this.lastMessageReceivedSequenceNumber}. This probably means the server restarted. Group: ${this.serverSideKey}`,
                    );
                    this.triggerSequenceNumberRegressionAlert();
                }
            }

            this.lastMessageReceivedTime = new Date();
            this.lastMessageReceivedSequenceNumber = sequenceNumber;
        }

        this.backendCallback(args.map((x: any) => this.type._FromServerJson(x)));
    };

    /**
     * We register the internal callback with the signalr connection
     */
    private async registerSignalrCallback() {
        if (this.backendMethodName != 'DbDataChangedNotification') {
            // This is only to remove flooding of the console, since everything register this, and it is an pointless logging
            console.log('Registering signalr callback for', this.backendMethodName);
        }

        try {
            await RegisterSignalrCallback(this.backendMethodName, this.signalrCallbackWrapper);
        } catch (e) {
            console.error('Could not register signalr callback: ', e);
        }
    }

    /**
     * We deregister the internal callback from the signalr connection
     */
    private async deregisterSignalrCallback() {
        console.log('Deregistering signalr callback for', this.backendMethodName);

        try {
            await DeregisterSignalrCallback(this.backendMethodName, this.signalrCallbackWrapper);
        } catch (e) {
            console.error('Could not deregister signalr callback: ', e);
        }
    }
}
